/**
 * EmailList — renders the email list pane from JSON signals.
 * Uses IntersectionObserver at the bottom sentinel to auto-load next page.
 * Groups rows by date (mirrors Hotwire email_date_group helper).
 */

import { h, Fragment } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { resolveFolderLabel } from '@tshmanager/email-shared'
import { EmailListRow } from './EmailListRow.jsx'
import { EmailSearchPanel } from './EmailSearch.jsx'
import { EmailBulkToolbar } from './EmailBulkToolbar.jsx'
import { groupEmailsByDate } from '../../modules/email/email-helpers.js'

const FOLDER_ICONS = {
  inbox:    'bi-inbox',
  starred:  'bi-star',
  sent:     'bi-send',
  drafts:   'bi-file-earmark',
  archived: 'bi-archive',
  spam:     'bi-exclamation-octagon',
  trash:    'bi-trash',
}

const QUICK_CHIPS = [
  { key: 'starred',    labelKey: 'emails.filter.starred',    icon: 'bi-star' },
  { key: 'attachment', labelKey: 'emails.filter.attachment', icon: 'bi-paperclip' },
]

const SORT_OPTIONS = [
  { value: 'newest',  labelKey: 'emails.sort.newest' },
  { value: 'oldest',  labelKey: 'emails.sort.oldest' },
  { value: 'sender',  labelKey: 'emails.sort.sender' },
  { value: 'subject', labelKey: 'emails.sort.subject' },
]

function emailListTimestamp(email) {
  return email.last_activity_at || email.last_activity || email.sent_at || email.created_at
}

export function EmailList({ store, i18n }) {
  const sentinelRef = useRef(null)
  const observerRef = useRef(null)
  const [searchOpen, setSearchOpen] = useState(false)

  const emails          = store.emails.value
  const meta            = store.emailsMeta.value
  const loading         = store.emailsLoading.value
  const loadingMore     = store.emailsLoadingMore.value
  const hasSelection    = store.hasSelection.value
  const currentThreadId = store.currentThreadId.value
  const activeMailbox   = store.activeMailbox.value
  const activeFolder    = store.activeFolder.value
  const mailboxes       = store.mailboxes.value
  const usesStackedNav  = store.usesStackedNav()
  const quickFilter     = store.quickFilter.value
  const searchText      = store.searchText.value
  const sortOrder       = store.sortOrder.value
  const advFilters      = store.advancedFilters.value
  const activeTab       = quickFilter === 'unread' ? 'unread' : 'all'

  const hasActiveFilters = searchText || quickFilter || sortOrder !== 'newest' ||
    Object.values(advFilters).some(v => v)

  const folderKey = Object.keys(FOLDER_ICONS).find(k => k === activeFolder)
  const folderIcon = folderKey ? FOLDER_ICONS[folderKey] : 'bi-folder'
  const folderLabel = folderKey
    ? i18n?.t(`emails.folders.${folderKey}`)
    : resolveFolderLabel(activeFolder, {
        emailFolders: store.emailFolders.value,
        emailTags:    store.emailTags.value,
      }) ?? activeFolder

  const activeMailboxInfo = activeMailbox !== null
    ? mailboxes.find(m => m.id === activeMailbox)
    : null

  useEffect(() => {
    if (hasActiveFilters) setSearchOpen(true)
  }, [hasActiveFilters])

  useEffect(() => {
    if (!sentinelRef.current) return

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && meta.next_page && !loadingMore) {
          store.fetchMoreEmails()
        }
      },
      { rootMargin: '200px' }
    )
    observerRef.current.observe(sentinelRef.current)

    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [sentinelRef.current, meta.next_page])

  function handleTab(tab) {
    store.setQuickFilter(tab === 'unread' ? 'unread' : '')
    store.fetchEmails(1)
  }

  function handleQuickFilter(chip) {
    store.toggleQuickFilter(chip)
    store.fetchEmails(1)
  }

  function handleSort(value) {
    store.setSort(value)
    store.fetchEmails(1)
  }

  function handleClearAll() {
    store.clearAllFilters()
    store.fetchEmails(1)
    setSearchOpen(false)
  }

  const dateGroups = groupEmailsByDate(emails, emailListTimestamp)

  return (
    <div class="email-list-pane-inner d-flex flex-column h-100">

      {/* Row 1: mailbox/folder context + search toggle + tools */}
      <div class="email-list-context-bar d-flex align-items-center gap-1 px-2 py-1 border-bottom">
        {usesStackedNav && (
          <button
            type="button"
            class="btn btn-sm btn-link text-muted p-0 flex-shrink-0 email-list-nav-toggle"
            onClick={() => store.showSidebarPanel()}
            title={i18n?.t('emails.folders_nav') || 'Mappák'}
          >
            <i class="bi bi-list" style="font-size:1.1rem" />
          </button>
        )}
        <i class={`bi ${folderIcon} text-muted flex-shrink-0`} style="font-size:0.8rem" />
        <span class="text-truncate flex-grow-1" style="font-size:0.8rem;color:var(--bs-secondary-color);font-weight:500">
          {activeMailboxInfo
            ? `${activeMailboxInfo.name} — ${folderLabel}`
            : `${i18n?.t('emails.all_mailboxes')} — ${folderLabel}`
          }
        </span>
        <button
          type="button"
          class={`btn btn-sm btn-link p-0 flex-shrink-0 email-list-search-toggle ${searchOpen || hasActiveFilters ? 'text-primary' : 'text-muted'}`}
          aria-expanded={searchOpen}
          title={i18n?.t('emails.search_placeholder')}
          onClick={() => setSearchOpen(v => !v)}
        >
          <i class="bi bi-search" style="font-size:0.95rem" />
        </button>
        <div class="dropdown flex-shrink-0">
          <button
            type="button"
            class="btn btn-sm btn-link text-muted p-0 email-list-sort-toggle"
            data-bs-toggle="dropdown"
            data-bs-strategy="fixed"
            title={i18n?.t('emails.sort.label')}
            aria-label={i18n?.t('emails.sort.label')}
          >
            <i class="bi bi-arrow-down-up" style="font-size:0.95rem" />
          </button>
          <ul class="dropdown-menu dropdown-menu-end shadow-sm">
            {SORT_OPTIONS.map(opt => (
              <li key={opt.value}>
                <button
                  type="button"
                  class={`dropdown-item small d-flex align-items-center gap-2 ${sortOrder === opt.value ? 'active' : ''}`}
                  onClick={() => handleSort(opt.value)}
                >
                  <i class={`bi bi-check flex-shrink-0 ${sortOrder === opt.value ? '' : 'invisible'}`} />
                  {i18n?.t(opt.labelKey)}
                </button>
              </li>
            ))}
          </ul>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            class="btn btn-sm btn-link p-0 flex-shrink-0 text-danger email-list-clear-filters"
            title={i18n?.t('emails.clear_filters')}
            onClick={handleClearAll}
          >
            <i class="bi bi-x-circle" style="font-size:0.95rem" />
          </button>
        )}
      </div>

      {/* Row 2: collapsible search panel */}
      {searchOpen && (
        <EmailSearchPanel store={store} i18n={i18n} />
      )}

      {/* Row 3: quick filter chips (starred / attachment) */}
      {!hasSelection && (
        <div class="email-list-quick-chips-row">
          {QUICK_CHIPS.map(chip => (
            <button
              key={chip.key}
              type="button"
              class={`btn btn-xs d-flex align-items-center gap-1 rounded-pill px-2 py-0 ${quickFilter === chip.key ? 'btn-primary' : 'btn-outline-secondary'}`}
              style="font-size:0.72rem;height:22px"
              onClick={() => handleQuickFilter(chip.key)}
            >
              <i class={`bi ${chip.icon}`} style="font-size:0.65rem" />
              {i18n?.t(chip.labelKey)}
            </button>
          ))}
        </div>
      )}

      {/* Row 4: All / Unread tabs */}
      {!hasSelection && (
        <div class="email-filter-tabs d-flex align-items-center border-bottom px-2">
          <button
            type="button"
            class={`email-filter-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTab('all')}
          >
            {i18n?.t('emails.filter_tabs.all')}
            <span
              class={`badge rounded-pill ms-1 ${activeTab === 'all' ? 'bg-primary' : 'bg-secondary-subtle text-secondary'}`}
              style="font-size:0.65rem"
            >
              {meta.total_count ?? 0}
            </span>
          </button>
          <button
            type="button"
            class={`email-filter-tab ${activeTab === 'unread' ? 'active' : ''}`}
            onClick={() => handleTab('unread')}
          >
            {i18n?.t('emails.filter_tabs.unread')}
            {(meta.unread_count ?? 0) > 0 && (
              <span class="badge rounded-pill ms-1 bg-primary" style="font-size:0.65rem">
                {meta.unread_count}
              </span>
            )}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-link p-0 text-muted email-list-select-all"
            title={i18n?.t('emails.select_all')}
            onClick={() => store.selectAll(emails.map(e => e.id))}
          >
            <i class="bi bi-check-all" />
          </button>
        </div>
      )}

      {hasSelection && <EmailBulkToolbar store={store} i18n={i18n} />}

      <div class="email-list-scroll flex-grow-1 overflow-auto" id="email_list_items">
        {loading ? (
          <div class="d-flex align-items-center justify-content-center py-5 text-muted">
            <div class="spinner-border spinner-border-sm me-2" role="status" />
            <span>{i18n?.t('helpers.loading')}</span>
          </div>
        ) : emails.length === 0 ? (
          <div class="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
            <i class="bi bi-inbox mb-2" style="font-size:2rem;opacity:0.4" />
            <span class="small">{i18n?.t('emails.empty_folder')}</span>
          </div>
        ) : (
          <>
            {dateGroups.map(group => (
              <Fragment key={group.key}>
                <div class="email-date-group-header px-3 py-1">
                  {i18n?.t(`emails.date_groups.${group.key}`)}
                </div>
                {group.emails.map(email => (
                  <EmailListRow
                    key={email.id}
                    email={email}
                    store={store}
                    isActive={email.id === currentThreadId}
                    i18n={i18n}
                  />
                ))}
              </Fragment>
            ))}

            {meta.next_page && (
              <div ref={sentinelRef} id="email_pagination" class="email-list-sentinel py-2 text-center text-muted">
                {loadingMore && (
                  <div class="spinner-border spinner-border-sm" role="status" />
                )}
              </div>
            )}
          </>
        )}
      </div>

    </div>
  )
}
